import React, {useState} from 'react';
import {LayoutChangeEvent, Platform, StyleSheet, useWindowDimensions, View} from 'react-native';

import {
  card,
  control as controlSize,
  layout,
  radius,
  universal,
} from '../../../design-system/tokens';
import theme from '../../../style/theme';

// react-native-web renders the app at the same breakpoint the rest of the web
// build treats as the desktop boundary: `ScreenDimensions.mobileOrTablet`.
// SHORTCUT: 768 repeats ScreenDimensions.mobileOrTablet.maxWidth
// (app/constants/ScreenDimensionsConfig.ts) as a literal. Ceiling: sign-up pages
// only. Upgrade path: read it from ScreenDimensions once DIS-1732 settles
// whether exactly 768px is wide here (`>=`) or narrow as elsewhere (`>`).
export const WIDE_BREAKPOINT = 768;

/**
 * Geometry for the primary CTA that sits in a sign-up page's `aside` slot.
 *
 * Exported rather than repeated per screen because components/ui-controls/Button
 * hardcodes @rneui's `size="sm"`, whose intrinsic height is 27px. A screen that
 * does not pin a height therefore renders a visibly smaller CTA than its
 * neighbour, which is exactly what happened to the create-account button while
 * the email step looked correct. Values: `control.ctaHeight` and
 * `radius.control` (DIS-1844 tokens).
 */
export const CTA_HEIGHT = controlSize.ctaHeight;
export const CTA_RADIUS = radius.control;

/**
 * Width state for a sign-up page, measured from the width the component is
 * actually given rather than from the window width, so a page renders correctly
 * inside a narrow Storybook frame on a desktop browser as well as in the app.
 *
 * `isWide` is width only: true at or above WIDE_BREAKPOINT on every platform.
 *
 * `isDesktopCard` is the layout decision callers pass to SignUpPageLayout. It is
 * `isWide` on web only. The desktop card is web-only: native renders the narrow
 * tree at every width, including iPad, which the app targets. A tablet
 * therefore keeps its narrow-only controls, such as the close X and AddToWallet.
 *
 * The width starts at the window width, so the first frame already picks the
 * final layout (DIS-1908: a 0 start drew the narrow, taller hero for one frame
 * on a wide web window). `onLayout` replaces it with the measured width. In a
 * narrow Storybook frame on a wide window the first frame is wide until then.
 *
 * Both are handed back to the caller instead of being hidden inside
 * SignUpPageLayout because callers need them themselves — the email step draws
 * a close icon only in the narrow layout.
 */
export function useWideLayout(): {
  isWide: boolean;
  isDesktopCard: boolean;
  onLayout: (event: LayoutChangeEvent) => void;
} {
  const [width, setWidth] = useState(useWindowDimensions().width);
  const isWide = width >= WIDE_BREAKPOINT;

  return {
    isWide,
    // Read at render, the same moment the callers read it before DIS-1852.
    isDesktopCard: isWide && Platform.OS === 'web',
    onLayout: (event: LayoutChangeEvent) => setWidth(event.nativeEvent.layout.width),
  };
}

export interface SignUpPageLayoutProps {
  isWide: boolean;
  onLayout: (event: LayoutChangeEvent) => void;
  /**
   * Full-width stack above the columns, spaced by the caller. Superseded by the
   * section slots below, which take their rhythm from the DIS-1844 tokens.
   */
  above?: React.ReactNode;
  /** Section slot: back or close control row, first in the centred section. */
  control?: React.ReactNode;
  /** Section slot: promo banner. Above the heading unless `bannerBelowIntro`. */
  banner?: React.ReactNode;
  /** Section slot: page heading. */
  heading?: React.ReactNode;
  /** Section slot: body copy under the heading. */
  intro?: React.ReactNode;
  /**
   * Puts the banner after the intro. Only the mobile Join frame (160:8335) draws
   * that order; every other sign-up frame puts the banner first.
   */
  bannerBelowIntro?: boolean;
  /**
   * Top padding of the section slots. Default: the width's
   * `layout.*.headerToContent`.
   */
  sectionPaddingTop?: number;
  /** flex 2 when wide; rendered first when stacked. */
  main?: React.ReactNode;
  /** flex 1 when wide; rendered second when stacked. */
  aside?: React.ReactNode;
  /** Full-width stack below the columns, e.g. trailing spacers. */
  below?: React.ReactNode;
  /**
   * Centred card only: a text block between `main` and `aside`, at the card's
   * text width (the same width as heading and intro), a section gap below
   * `main` and a group gap above `aside`. Ignored by the other arrangements.
   */
  outro?: React.ReactNode;
  /**
   * 'bottom' bottom-aligns the row so a CTA in `aside` sits level with an input
   * in `main` rather than with the input's label. 'top' for a tall aside column.
   */
  asideAlign?: 'top' | 'bottom';
  /** Gap between main and aside when stacked. */
  stackedGap?: number;
  /**
   * Desktop card arrangement for a single-column step (Figma D1 Welcome 536:4247,
   * D2 Welcome Back 536:4455). Wide: banner across the column, then a centred
   * card with heading and intro, and `main` then `aside` in the form column;
   * `control` sits outside the card at the column's left edge. Narrow: the same
   * stacked rendering as without it.
   */
  centred?: boolean;
  testID?: string;
}

/**
 * The responsive two-column shell shared by the sign-up pages.
 *
 * Owns the screen background, the gutter and max-width content box, and the
 * wide-versus-stacked arrangement of `main` and `aside`. It owns no scroll
 * container: SignUpFlowContent wraps the email and password steps in a
 * ScrollView on web (DIS-1859), and the create-account page supplies its own
 * KeyboardAwareScrollView around this layout.
 */
export default function SignUpPageLayout({
  isWide,
  onLayout,
  above,
  control,
  banner,
  heading,
  intro,
  bannerBelowIntro = false,
  sectionPaddingTop,
  main,
  aside,
  below,
  outro,
  asideAlign = 'bottom',
  stackedGap = 24,
  centred = false,
  testID,
}: SignUpPageLayoutProps): React.JSX.Element {
  const rhythm = isWide ? layout.desktop : layout.mobile;
  const hasSection = Boolean(control || banner || heading || intro);
  const bannerFirst = Boolean(banner) && !bannerBelowIntro;
  const bannerLast = Boolean(banner) && bannerBelowIntro;

  // Header -> first item, banner <-> heading, heading -> intro, then the
  // columns. A banner that closes the section is a group gap from the form
  // (160:8335 measures 24), otherwise the intro is a section gap from it.
  const section = hasSection ? (
    <View
      style={{
        paddingTop: sectionPaddingTop ?? rhythm.headerToContent,
        paddingBottom: bannerLast ? rhythm.stackGroup : rhythm.stackSection,
      }}>
      {control ? <View style={{marginBottom: rhythm.stackRelated}}>{control}</View> : null}
      {bannerFirst ? <View style={{marginBottom: rhythm.stackGroup}}>{banner}</View> : null}
      {heading}
      {intro ? <View style={{marginTop: rhythm.stackRelated}}>{intro}</View> : null}
      {bannerLast ? <View style={{marginTop: rhythm.stackGroup}}>{banner}</View> : null}
    </View>
  ) : null;

  if (centred && isWide) {
    return (
      <View style={styles.screen} onLayout={onLayout} testID={testID}>
        <View style={[styles.content, styles.contentWide, styles.centredPage]}>
          {above}
          {banner ? <View style={styles.centredBanner}>{banner}</View> : null}
          <View>
            {control ? <View style={styles.centredControl}>{control}</View> : null}
            <View style={styles.card} testID={testID ? `${testID}-card` : undefined}>
              {heading || intro ? (
                <View style={styles.cardText} testID={testID ? `${testID}-text` : undefined}>
                  {heading}
                  {intro ? <View style={styles.cardIntro}>{intro}</View> : null}
                </View>
              ) : null}
              {/* main, then the outro if present, then the gap, then aside. */}
              <View style={styles.cardForm} testID={testID ? `${testID}-form` : undefined}>
                {main}
                {!outro && main && aside ? <View style={styles.cardFormGap} /> : null}
                {outro ? null : aside}
              </View>
              {outro ? (
                <View style={styles.cardOutro} testID={testID ? `${testID}-outro` : undefined}>
                  {outro}
                </View>
              ) : null}
              {outro && aside ? (
                <View style={styles.cardForm} testID={testID ? `${testID}-aside` : undefined}>
                  {aside}
                </View>
              ) : null}
            </View>
          </View>
          {below}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen} onLayout={onLayout} testID={testID}>
      <View style={[styles.content, isWide && styles.contentWide]}>
        {above}
        {section}

        {isWide ? (
          <View style={asideAlign === 'top' ? styles.actionRowTop : styles.actionRow}>
            <View style={styles.actionField}>{main}</View>
            <View style={styles.actionCta}>{aside}</View>
          </View>
        ) : (
          <>
            {main}
            {main && aside ? <View style={{height: stackedGap}} /> : null}
            {aside}
          </>
        )}

        {below}
      </View>
    </View>
  );
}

// SHORTCUT: 40 is a literal: the space below the last control on every
// sign-up page that uses the stacked or two-column arrangement, kept in one
// place. It is not layout.*.pageEnd (64), which only the centred card uses.
// Ceiling: the sign-up page shell. Upgrade path: repoint it at a layout token
// once design confirms one page-end value for every sign-up page.
const CONTENT_BOTTOM_PADDING = 40;
// SHORTCUT: spacing literals - COLUMN_GAP and the stackedGap default 24 in
// SignUpPageLayout's props. Ceiling: every sign-up page shell. Upgrade path:
// the same spacing module as CONTENT_BOTTOM_PADDING above. GUTTER is
// layout.*.gutter, 16 on both widths.
const GUTTER = layout.mobile.gutter;
const COLUMN_GAP = 40;
// SHORTCUT: 1320 repeats the web ContentView column (ContentView.tsx, web
// `maxWidth: '1320px'` with `paddingHorizontal: 16`), so a sign-up page lines up
// with every other page: a 1320 box, 16 gutters, 1288 usable, centred. Figma
// 536:4602 draws 1288 at x76 and 537:5246 keeps it at 2048. No shared constant
// exists; ContentView, BrandHeader and StoreNameCard each repeat 1320. Ceiling:
// sign-up pages. Upgrade path: one exported page-width constant those files share.
const CONTENT_MAX_WIDTH = 1320;

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: theme.colour.background.general.screen,
  },
  content: {
    width: '100%',
    paddingHorizontal: GUTTER,
    paddingBottom: CONTENT_BOTTOM_PADDING,
  },
  contentWide: {
    alignSelf: 'center',
    maxWidth: CONTENT_MAX_WIDTH,
  },
  // A field label sits above its input, so the row is bottom-aligned to keep the
  // CTA level with the input rather than with the label.
  actionRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: COLUMN_GAP,
  },
  actionRowTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: COLUMN_GAP,
  },
  actionField: {
    flex: 2,
  },
  // Centred (card) arrangement, wide only. Header -> first block, banner ->
  // card, heading -> intro, intro -> form, field -> CTA and page end all come
  // from layout.desktop; the card from `card`, `radius`, `control`, `universal`.
  centredPage: {
    paddingTop: layout.desktop.headerToContent,
    paddingBottom: layout.desktop.pageEnd,
  },
  centredBanner: {
    marginBottom: layout.desktop.stackGroup,
  },
  // Outside the card, at the column's left edge, level with the card's top.
  centredControl: {
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: 1,
  },
  card: {
    alignSelf: 'center',
    width: card.width,
    maxWidth: '100%',
    paddingVertical: card.paddingY,
    borderWidth: controlSize.borderWidth,
    borderColor: universal.greyMedium,
    borderRadius: radius.card,
  },
  cardText: {
    paddingHorizontal: card.paddingX,
    marginBottom: layout.desktop.stackSection,
  },
  cardIntro: {
    marginTop: layout.desktop.stackRelated,
  },
  cardForm: {
    alignSelf: 'center',
    width: card.formWidth,
    maxWidth: '100%',
  },
  cardFormGap: {
    height: layout.desktop.stackGroup,
  },
  // Same width as cardText: card.paddingX in from each side. main -> outro is
  // a section gap, outro -> aside a group gap.
  cardOutro: {
    paddingHorizontal: card.paddingX,
    marginTop: layout.desktop.stackSection,
    marginBottom: layout.desktop.stackGroup,
  },
  actionCta: {
    flex: 1,
  },
});
